import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelopeCircleCheck, faCircleExclamation, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useTheme } from '../contexts/ThemeContext';
import authService from '../services/authService';
import './Unsubscribe.css';

function Unsubscribe() {
  console.log('[Unsubscribe] Component mounting...');
  const [searchParams] = useSearchParams();
  
  // Temporarily try/catch useTheme in case it's the issue
  let isDarkMode = true;
  try {
    const theme = useTheme();
    isDarkMode = theme?.isDarkMode ?? true;
    console.log('[Unsubscribe] Theme loaded:', isDarkMode);
  } catch (e) {
    console.error('[Unsubscribe] Theme error:', e);
  }
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [message, setMessage] = useState("");

  console.log('[Unsubscribe] Current state:', { isLoading, isSuccess, message, isDarkMode });

  useEffect(() => {
    console.log('[Unsubscribe] useEffect running...');
    const token = searchParams.get('token');
    console.log('[Unsubscribe] Token from URL:', token);
    
    if (!token) {
      console.log('[Unsubscribe] No token found, showing error');
      setIsLoading(false);
      setIsSuccess(false);
      setMessage("Invalid or missing unsubscribe token. Please check your email link.");
      return;
    }

    // Call the unsubscribe API
    const unsubscribe = async () => {
      console.log('[Unsubscribe] Calling API with token:', token);
      try {
        const response = await authService.unsubscribe(token);
        console.log('[Unsubscribe] API response:', response);
        setIsSuccess(true);
        // Backend returns { Message: "..." } with capital M
        setMessage(response.Message || response.message || "You have been successfully unsubscribed from promotional emails.");
      } catch (error) {
        console.error('[Unsubscribe] API error:', error);
        setIsSuccess(false);
        // Error data is in error.data (set by fetchFromApi), with capital M from backend
        const errorMessage = error.data?.Message || 
                            error.data?.message ||
                            error.message || 
                            "An error occurred while processing your request. Please try again.";
        let displayMessage = errorMessage;
        if (errorMessage === "Invalid or malformed token.") {
          displayMessage = "Invalid or malformed token.<br />If you think this is by error, you can login and unsubscribe from the settings page.";
        }
        setMessage(displayMessage);
      } finally {
        console.log('[Unsubscribe] API call finished, setting isLoading to false');
        setIsLoading(false);
      }
    };

    unsubscribe();
  }, [searchParams]);

  // DEBUG: Add a visible test to confirm component renders
  console.log('[Unsubscribe] About to render JSX');

  const themeClass = isDarkMode ? 'dark-mode' : 'light-mode';

  return (
    <div className={`unsubscribe-page-container ${themeClass}`} style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#1a1a2e' }}>
      <div className="unsubscribe-container-main" style={{ background: '#16213e', padding: '40px', borderRadius: '15px', textAlign: 'center', color: 'white' }}>
        {isLoading ? (
          <div className="unsubscribe-loading">
            <FontAwesomeIcon icon={faSpinner} spin className="unsubscribe-spinner" style={{ fontSize: '48px', color: '#3498db' }} />
            <p className="unsubscribe-loading-text" style={{ marginTop: '20px' }}>Processing your request...</p>
          </div>
        ) : (
          <>
            <div className="unsubscribe-icon-wrapper">
              <FontAwesomeIcon 
                icon={isSuccess ? faEnvelopeCircleCheck : faCircleExclamation} 
                className={`unsubscribe-icon ${isSuccess ? 'success' : 'error'}`}
                style={{ fontSize: '64px', color: isSuccess ? '#28a745' : '#dc3545' }}
              />
            </div>
            
            <h1 className="unsubscribe-title" style={{ marginTop: '20px', fontSize: '1.8rem' }}>
              {isSuccess ? "Sorry to See You Go" : "Something Went Wrong"}
            </h1>
            
            <p className="unsubscribe-message" dangerouslySetInnerHTML={{ __html: message }}></p>
            
            {isSuccess && (
              <p className="unsubscribe-note" style={{ marginTop: '16px', fontStyle: 'italic', opacity: 0.8 }}>
                You can always manage your email preferences in your account settings.
              </p>
            )}
            
            <div className="unsubscribe-actions" style={{ marginTop: '24px' }}>
              <Link to="/login" className="unsubscribe-link" style={{ 
                display: 'inline-block',
                padding: '12px 28px',
                background: '#3498db',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '8px'
              }}>
                Go to Login
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Unsubscribe;
